/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
	Form,
	Input,
	Select,
	Button,
	Divider,
	Tag,
	Tooltip,
	Alert,
	Dropdown,
	Space,
} from 'antd';

import { LockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useForm } from 'antd/es/form/Form';
import TipTapEditor from './TipTapEditor';
import RecipientSelect from './RecipientSelect';
import TRIGGER_KEYS from './emailTemplateConstants';

// eslint-disable-next-line no-template-curly-in-string
const SUBJECT_PLACEHOLDER = 'e.g. Welcome to ${tenantName}';

const EmailTemplateForm = ({
	template,
	onSave,
	onCancel,
	onDelete,
	saving,
	unsavedChanges,
	setUnsavedChanges,
}) => {
	const [form] = useForm();
	const subjectRef = useRef(null);
	const subjectCursorRef = useRef(null);
	const [bodyHtml, setBodyHtml] = useState(template?.body || '');
	const [footerHtml, setFooterHtml] = useState(template?.footer || '');
	const [selectedTriggerKey, setSelectedTriggerKey] = useState(template?.triggerKey || null);
	const variablesCatalog = useSelector((state) => state.emailTemplates.templateVariables.catalog);

	const insertSubjectVariable = (varName) => {
		const token = `\${${varName}}`;
		const current = form.getFieldValue('subject') || '';
		const pos = subjectCursorRef.current ?? current.length;
		const next = current.slice(0, pos) + token + current.slice(pos);
		form.setFieldsValue({ subject: next });
		setUnsavedChanges(true);
		const newPos = pos + token.length;
		subjectCursorRef.current = newPos;
		setTimeout(() => {
			const input = subjectRef.current?.input;
			if (input) {
				input.focus();
				input.setSelectionRange(newPos, newPos);
			}
		}, 0);
	};

	useEffect(() => {
		form.setFieldsValue({
			templateName: template?.templateName || '',
			triggerKey: template?.triggerKey || undefined,
			subject: template?.subject || '',
			recipients: template?.recipients || [],
		});
		setBodyHtml(template?.body || '');
		setFooterHtml(template?.footer || '');
		setSelectedTriggerKey(template?.triggerKey || null);
	}, [template]);

	const availableVariables = selectedTriggerKey
		? (variablesCatalog.find((e) => e.triggerKey === selectedTriggerKey)?.variables || [])
		: [];

	const handleFinish = (values) => {
		onSave({
			...values,
			body: bodyHtml,
			footer: footerHtml,
			triggerKey: template ? template.triggerKey : values.triggerKey,
		});
	};

	return (
		<Form
			form={form}
			layout='vertical'
			onFinish={handleFinish}
			onValuesChange={() => setUnsavedChanges(true)}>

			<Form.Item
				label='Template Name'
				name='templateName'
				rules={[{ required: true, message: 'Please enter a template name' }]}>
				<Input placeholder='e.g. Driver Welcome Email' />
			</Form.Item>

			<Form.Item
				label={
					<span>
						Trigger Key&nbsp;
						<Tooltip title='System identifier that links this template to an automated event. Cannot be changed after creation.'>
							<InfoCircleOutlined style={{ color: '#8c8c8c' }} />
						</Tooltip>
					</span>
				}
				name='triggerKey'>
				{template ? (
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<Tag icon={<LockOutlined />} color='default' style={{ fontSize: 13, padding: '2px 8px' }}>
							{template.triggerKey || 'None (manual only)'}
						</Tag>
						<span style={{ color: '#8c8c8c', fontSize: 12 }}>Cannot be changed after creation</span>
					</div>
				) : (
					<Select
						allowClear
						placeholder='Select a trigger (optional)'
						onChange={(val) => {
							setSelectedTriggerKey(val || null);
							setUnsavedChanges(true);
						}}
						options={TRIGGER_KEYS}
					/>
				)}
			</Form.Item>

			{availableVariables.length > 0 && (
				<Alert
					type='info'
					style={{ marginBottom: 16 }}
					message={
						<div>
							<strong>Available variables for this trigger:</strong>
							<div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
								{availableVariables.map((v) => (
									<Tag key={v} color='blue' style={{ fontFamily: 'monospace' }}>{v}</Tag>
								))}
							</div>
						</div>
					}
				/>
			)}

			<Form.Item label='Subject' required>
				<Space.Compact style={{ width: '100%' }}>
					<Form.Item
						name='subject'
						noStyle
						rules={[{ required: true, message: 'Please enter a subject' }]}>
						<Input
							ref={subjectRef}
							placeholder={SUBJECT_PLACEHOLDER}
							onBlur={(e) => { subjectCursorRef.current = e.target.selectionStart; }}
							onClick={(e) => { subjectCursorRef.current = e.target.selectionStart; }}
							onKeyUp={(e) => { subjectCursorRef.current = e.target.selectionStart; }}
						/>
					</Form.Item>
					{availableVariables.length > 0 && (
						<Dropdown
							trigger={['click']}
							menu={{
								items: availableVariables.map((v) => ({
									key: v,
									label: <Tag color='blue' style={{ fontFamily: 'monospace', cursor: 'pointer' }}>{v}</Tag>,
									onClick: () => insertSubjectVariable(v),
								})),
							}}>
							<Button>{'{ }'}</Button>
						</Dropdown>
					)}
				</Space.Compact>
			</Form.Item>

			<Form.Item label='Body' required>
				<TipTapEditor
					key={template?.id ?? 'new'}
					value={bodyHtml}
					onChange={(html) => {
						setBodyHtml(html);
						setUnsavedChanges(true);
					}}
					variables={availableVariables}
				/>
			</Form.Item>

			<Form.Item label='Footer'>
				<TipTapEditor
					key={`footer-${template?.id ?? 'new'}`}
					value={footerHtml}
					onChange={(html) => {
						setFooterHtml(html);
						setUnsavedChanges(true);
					}}
					variables={availableVariables}
				/>
			</Form.Item>

			<Form.Item
				label={
					<span>
						Recipients&nbsp;
						<Tooltip title='Optional list of users to notify for cron-triggered templates. For most event-driven templates (welcome, application, etc.) the recipients are determined by the flow and this field is ignored.'>
							<InfoCircleOutlined style={{ color: '#8c8c8c' }} />
						</Tooltip>
					</span>
				}
				name='recipients'>
				<RecipientSelect />
			</Form.Item>

			<Divider />
			<Form.Item style={{ float: 'right', marginBottom: 32 }}>
				{template && onDelete && (
					<Button danger style={{ marginRight: 8 }} onClick={onDelete}>
						Delete
					</Button>
				)}
				<Button style={{ marginRight: 8 }} onClick={() => onCancel(unsavedChanges)}>
					Cancel
				</Button>
				<Button type='primary' htmlType='submit' loading={saving} disabled={!unsavedChanges}>
					{template ? 'Update' : 'Save'}
				</Button>
			</Form.Item>
		</Form>
	);
};

export default EmailTemplateForm;
